import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The white block behind the current page in the rail, which slides.
 *
 * One element, moved with `transform` and `height`. It replaces a background
 * that was painted on whichever link happened to be active, which meant the
 * highlight vanished from one row and appeared on another with nothing in
 * between — so the eye had to find it again on every navigation.
 *
 * ## Why transform and not top
 *
 * `transform` and `opacity` are the only two properties a browser can animate
 * without laying the page out again. Animating `top` would recalculate layout on
 * every frame of every navigation, and the machines this runs on are cheap
 * Android phones and an office laptop — the two places where that is the
 * difference between a slide and a stutter.
 *
 * `height` is not on that list, and is animated here anyway: rail rows are all
 * 44px, so it only changes when the measurement is first taken. In practice it
 * is set once and then only translated.
 *
 * ## It is decoration, and behaves like it
 *
 * The active link still carries its own text colour, and still announces itself
 * with `aria-current`. This block is `aria-hidden` and sits behind the links; if
 * the measurement fails, nothing is lost but the animation. That is deliberate —
 * an indicator that is the *only* signal of where you are becomes a bug the
 * moment it mismeasures.
 */
export function ActiveMarker({ scope }: { scope: React.RefObject<HTMLUListElement | null> }) {
  const [box, setBox] = useState<{ top: number; height: number } | null>(null);
  /** Suppresses the transition on the very first placement. */
  const settled = useRef(false);
  const location = useLocation();

  const measure = useCallback(() => {
    const root = scope.current;
    if (!root) return;

    /*
     * `aria-current="page"` rather than a class name.
     *
     * React Router sets it on the active NavLink, so it is the same fact the
     * screen reader uses. Matching on a Tailwind class would break silently the
     * next time the active styling is edited — and would have already broken
     * once, when the active background moved out of the link and into this.
     */
    const active = root.querySelector<HTMLElement>('a[aria-current="page"]');
    if (!active) {
      setBox(null);
      return;
    }

    /*
     * Offsets, not bounding rects.
     *
     * The rail scrolls its own contents. `getBoundingClientRect` is relative to
     * the viewport, so it would drift by the scroll amount and the block would
     * lag behind the row it belongs to. `offsetTop` walked up to the scope is
     * relative to the scrolling content, which is what this is positioned
     * inside — the list, which is this element's own offset parent.
     */
    let top = 0;
    let node: HTMLElement | null = active;
    while (node && node !== root) {
      top += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }

    setBox({ top, height: active.offsetHeight });
  }, [scope]);

  /* Where we are changed, or a category opened and moved everything below it. */
  useEffect(() => {
    measure();
    // A frame later as well: on a navigation that also expands a category, the
    // first measurement lands before the new rows have been laid out.
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, measure]);

  /*
   * The rail's own contents can change height without a navigation — expanding
   * a category, or the window resizing and the rail re-wrapping. A
   * ResizeObserver on the scope catches both without polling.
   */
  useEffect(() => {
    const root = scope.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(root);
    return () => observer.disconnect();
  }, [scope, measure]);

  useEffect(() => {
    if (box) settled.current = true;
  }, [box]);

  if (!box) return null;

  return (
    <span
      aria-hidden="true"
      style={{
        transform: `translateY(${box.top}px)`,
        height: box.height,
        // Only after the first placement. Sliding in from zero on page load
        // would draw the eye to the navigation instead of to the page.
        transitionProperty: settled.current ? 'transform, height' : 'none',
      }}
      className="pointer-events-none absolute inset-x-0 top-0 z-0 rounded-lg bg-card
        duration-200 ease-out motion-reduce:transition-none"
    />
  );
}
